import {
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { GetEvidenceQuery, UpdateEvidenceData } from "./evidence.api.types";
import { UseGetInfiniteEvidence } from "./evidence.query.types";
import { EvidenceAPI } from "./evidence.api";
import { Evidence } from "./evidence.types";

const evidenceQueryKeys = {
  all: ["evidence", "list"],
  list: (q: GetEvidenceQuery) => ["evidence", "list", q],
  infiniteList: (q: GetEvidenceQuery) => ["evidence", "infinite-list", q],
  details: (id: number) => ["evidence", "list", { id }],
};

const useGetInfiniteEvidence: UseGetInfiniteEvidence = (q, options) => {
  return useInfiniteQuery({
    queryKey: evidenceQueryKeys.infiniteList(q),
    queryFn: ({ pageParam }) =>
      EvidenceAPI.query({
        ...q,
        page: pageParam as number,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage?.page) return null;
      if (firstPage?.page - 1 === 0) return null;
      return firstPage?.page - 1;
    },
    initialPageParam: 1,
    placeholderData: (data) => data,
    ...options,
  });
};

const updateInfiniteEvidenceCache = (options: {
  queryClient: QueryClient;
  queryKey: QueryKey;
  updateData: Omit<Partial<Evidence>, "evidence_id"> & { evidence_id: number };
}) => {
  const { queryClient, queryKey, updateData } = options;
  const previousInfiniteData: any = queryClient.getQueryData(queryKey);

  if (!previousInfiniteData) {
    return { previous: null, new: null };
  }

  const newInfiniteData = {
    ...previousInfiniteData,
    pages: previousInfiniteData.pages.map((page: { data: Evidence[] }) => {
      return {
        ...page,
        data: page.data.map((item: Evidence) => {
          if (item.evidence_id === updateData.evidence_id) {
            return { ...item, ...updateData };
          }
          return item;
        }),
      };
    }),
  };

  queryClient.setQueryData(queryKey, (old: any) => newInfiniteData);
  return { previous: previousInfiniteData, new: newInfiniteData };
};

const updateEvidenceListCache = (options: {
  queryClient: QueryClient;
  query: GetEvidenceQuery;
  updateData: Omit<Partial<Evidence>, "evidence_id"> & { evidence_id: number };
}) => {
  const { queryClient, updateData, query } = options;
  const queryKey = evidenceQueryKeys.list(query);
  const previousData: any = queryClient.getQueryData(queryKey);

  if (!previousData) {
    return { previous: null, new: null };
  }

  const newData = {
    ...previousData,
    data: previousData.data.map((item: Evidence) => {
      if (item.evidence_id === updateData.evidence_id) {
        return { ...item, ...updateData };
      }
      return item;
    }),
  };
  queryClient.setQueryData(queryKey, newData);
  return { previous: previousData, new: newData };
};

const useMutateUpdateEvidence = (options: { query: GetEvidenceQuery }) => {
  const { query } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateEvidenceData) => {
      return EvidenceAPI.update(data);
    },
    onMutate: async (updateData) => {
      try {
        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: evidenceQueryKeys.list(query),
        });
        await queryClient.cancelQueries({
          queryKey: evidenceQueryKeys.infiniteList(query),
        });

        const { previous: previousInfiniteData, new: newInfiniteData } =
          updateInfiniteEvidenceCache({
            queryClient,
            queryKey: evidenceQueryKeys.infiniteList(query),
            updateData: updateData,
          });

        const { previous: previousListData, new: newListData } =
          updateEvidenceListCache({
            queryClient,
            query,
            updateData: updateData,
          });

        // Return a result with the snapshotted values
        return {
          previousListData,
          previousInfiniteData,
          newListData,
          newInfiniteData,
        };
      } catch (error) {
        console.log("error", error);
      }
    },
    onError: (err, updateData, onMutateResult) => {
      if (onMutateResult) {
        queryClient.setQueryData(
          evidenceQueryKeys.list(query),
          onMutateResult.previousListData
        );
        queryClient.setQueryData(
          evidenceQueryKeys.infiniteList(query),
          onMutateResult.previousInfiniteData
        );
      }
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: evidenceQueryKeys.list(query),
      });
      queryClient.invalidateQueries({
        queryKey: evidenceQueryKeys.infiniteList(query),
      });
    },
  });
};

export const EvidenceQuery = {
  evidenceQueryKeys,
  queryKeys: evidenceQueryKeys,
  useGetInfiniteEvidence,
  useMutateUpdateEvidence,
  updateEvidenceListCache,
  updateInfiniteEvidenceCache,
};
